import {
    ForwardRefRenderFunction,
    PropsWithChildren,
    RefObject,
    createContext,
    forwardRef,
    useImperativeHandle,
    useRef,
} from 'react';

import { CustomScrollEvent, CustomScrollHandler } from '@hh.ru/magritte-internal-custom-scroll/types';

export interface CustomScrollContextProps {
    subscribe: (handler: CustomScrollHandler) => void;
    unsubscribe: (handler: CustomScrollHandler) => void;
    wrapperRef: RefObject<HTMLDivElement>;
}

export const CustomScrollContext = createContext<CustomScrollContextProps | null>(null);

export interface CustomScrollContextNotifier {
    notify: (event: Partial<CustomScrollEvent>) => void;
}

const CustomScrollContextProviderRenderFunc: ForwardRefRenderFunction<
    CustomScrollContextNotifier,
    PropsWithChildren
> = ({ children }, ref) => {
    const handlersRef = useRef<CustomScrollHandler[]>([]);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const valueRef = useRef<CustomScrollContextProps>(
        (() => {
            return {
                subscribe: (handler) => {
                    handlersRef.current.push(handler);
                },
                unsubscribe: (handlerToRemove) => {
                    handlersRef.current = handlersRef.current.filter((handler) => handler !== handlerToRemove);
                },
                wrapperRef,
            };
        })()
    );

    useImperativeHandle(
        ref,
        () => ({
            notify: ({ scrollTop = 0, scrollLeft = 0, maxScrollTop = 0, maxScrollLeft = 0 }) => {
                handlersRef.current.forEach((handler) =>
                    handler({ scrollTop, scrollLeft, maxScrollTop, maxScrollLeft })
                );
            },
        }),
        []
    );

    return (
        <CustomScrollContext.Provider value={valueRef.current}>
            <div style={{ display: 'contents' }} ref={wrapperRef}>
                {children}
            </div>
        </CustomScrollContext.Provider>
    );
};

export const CustomScrollContextProvider = forwardRef(CustomScrollContextProviderRenderFunc);
